import React, { useEffect, useState } from 'react';
import { Sparkles, TrendingUp, Award, Crown } from 'lucide-react';
import { getUserRank, calculateLevelInfo } from '../utils/levelCalculation';

interface UserLevelUpCelebrationProps {
  userName: string;
  avatarUrl: string | null;
  oldLevel: number;
  newLevel: number;
  oldTotalLevel: number;
  newTotalLevel: number;
  newPoints: number;
  onClose: () => void;
}

export function UserLevelUpCelebration({
  userName,
  avatarUrl,
  oldLevel,
  newLevel,
  oldTotalLevel,
  newTotalLevel,
  newPoints,
  onClose
}: UserLevelUpCelebrationProps) {
  const [showAnimation, setShowAnimation] = useState(false);
  const oldRank = getUserRank(oldTotalLevel);
  const newRank = getUserRank(newTotalLevel);
  const rankChanged = oldRank.name !== newRank.name;

  const levelInfo = calculateLevelInfo(newPoints);
  const tierChanged = oldTotalLevel < 100 && newTotalLevel >= 100;

  useEffect(() => {
    setTimeout(() => setShowAnimation(true), 100);
  }, []);

  const getGradientColor = () => {
    return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur flex items-center justify-center p-4">
      <div
        className={`max-w-lg w-full bg-card border-2 rounded-2xl p-8 space-y-6 transform transition-all duration-500 ${
          showAnimation ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{
          borderImage: getGradientColor(),
          borderImageSlice: 1
        }}
      >
        <div className="text-center space-y-4">
          <div className="relative inline-block">
            <div
              className="absolute inset-0 rounded-full blur-xl opacity-50 animate-pulse"
              style={{
                background: getGradientColor()
              }}
            />
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={userName}
                className="relative w-24 h-24 rounded-full object-cover ring-4 ring-white/20"
              />
            ) : (
              <div
                className="relative w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold"
                style={{
                  background: getGradientColor()
                }}
              >
                {userName.split(' ').map(part => part[0]).join('').toUpperCase().slice(0, 2)}
              </div>
            )}
            <div
              className="absolute -top-2 -right-2 w-10 h-10 rounded-full flex items-center justify-center animate-bounce"
              style={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
              }}
            >
              <Sparkles className="w-5 h-5 text-white" />
            </div>
          </div>

          <div className="space-y-2">
            <h3
              className="text-3xl font-bold"
              style={{
                background: getGradientColor(),
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {userName}
            </h3>
            <h2 className="text-3xl font-bold text-foreground">
              {tierChanged ? '🎊 New Tier Unlocked! 🎊' : 'Level Up!'}
            </h2>
            <p className="text-muted-foreground">
              Your total level has increased
            </p>
          </div>

          {rankChanged && (
            <div className="flex items-center justify-center gap-4 py-6">
              <div className="text-center space-y-1">
                <div className="text-sm text-muted-foreground">Previous Rank</div>
                <div
                  className="px-4 py-2 rounded-lg font-medium text-white"
                  style={{ background: 'linear-gradient(135deg, #a8a8a8 0%, #6b6b6b 100%)' }}
                >
                  {oldRank.name}
                </div>
              </div>

              <TrendingUp className="w-6 h-6 text-muted-foreground" />

              <div className="text-center space-y-1">
                <div className="text-sm text-muted-foreground">New Rank</div>
                <div
                  className="px-4 py-2 rounded-lg font-bold animate-pulse text-white"
                  style={{ background: getGradientColor() }}
                >
                  {newRank.name}
                </div>
              </div>
            </div>
          )}

          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Level</span>
              <span className="font-bold text-foreground">{newLevel}</span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Tier</span>
              <span
                className="font-bold"
                style={{
                  background: getGradientColor(),
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {levelInfo.tier} {levelInfo.level}
              </span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Total Level</span>
              <span className="font-bold text-foreground">{newTotalLevel}</span>
            </div>

            <div className="space-y-1 pt-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Progress to Next Level</span>
                <span className="text-muted-foreground">
                  {Math.round(levelInfo.progress)}%
                </span>
              </div>
              <div className="h-2 bg-background rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-1000"
                  style={{
                    width: `${levelInfo.progress}%`,
                    background: getGradientColor()
                  }}
                />
              </div>
            </div>

            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {levelInfo.currentPoints} / {levelInfo.pointsForNextLevel} XP
              </span>
              <span className="text-muted-foreground">
                {levelInfo.pointsToNextLevel} to go
              </span>
            </div>
          </div>

          {newLevel === 1 && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <p className="text-sm text-blue-600 font-medium">
                🎯 Your journey begins! First overall level achieved.
              </p>
            </div>
          )}

          {newLevel === 10 && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
              <p className="text-sm text-green-600 font-medium">
                🌟 Level 10! You're building serious momentum across all domains.
              </p>
            </div>
          )}

          {newLevel === 25 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              <p className="text-sm text-amber-600 font-medium">
                ⭐ Quarter century! Your dedication is truly remarkable.
              </p>
            </div>
          )}

          {newLevel === 50 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              <p className="text-sm text-amber-600 font-medium">
                🏅 Halfway to tier mastery! You're on an incredible journey.
              </p>
            </div>
          )}

          {newLevel === 100 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              <p className="text-sm text-amber-600 font-medium">
                🏆 Tier complete! You've mastered the {levelInfo.tier} tier!
              </p>
            </div>
          )}

          {tierChanged && (
            <div className="flex items-center justify-center gap-2 text-amber-500 py-2">
              <Award className="w-5 h-5" />
              <span className="font-bold text-sm">New Tier Achievement!</span>
              <Award className="w-5 h-5" />
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="w-full py-3 rounded-lg font-medium text-white transition-all hover:scale-105"
          style={{ background: getGradientColor() }}
        >
          Continue
        </button>
      </div>

      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: '-20px',
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${3 + Math.random() * 2}s`,
              fontSize: `${20 + Math.random() * 20}px`,
              opacity: 0.7
            }}
          >
            {['✨', '⭐', '🌟', '💫', '👑', '🏆'][Math.floor(Math.random() * 6)]}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes float {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 0.7;
          }
          90% {
            opacity: 0.7;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
        .animate-float {
          animation: float linear infinite;
        }
      `}</style>
    </div>
  );
}
